import React, { FC } from 'react';

import { Box, styled } from '@mui/material';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import { useDiscoverContext } from '~/context/DiscoverContext';
import { useTenantContext } from '~/context/TenantProvider';
import useContentAvailability from '~/hooks/useContentAvailability';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import TabItem from '~/pages/DiscoverPage/components/TabItem/TabItem';
import { HITS_TYPES } from '~/pages/const';
import {
  DISCOVER_COURSES_ROUTE,
  DISCOVER_GLOSSARY_ROUTE,
  DISCOVER_INSIGHTS_ROUTE,
  DISCOVER_PATHWAYS_ROUTE,
  DISCOVER_SEARCH_ROUTE,
  DISCOVER_VIDEOS_ROUTE,
} from '~/routes';
import { MIDDLE_MARGIN_PX, SKELETON_TEXT_HEIGHT_PX, SMALL_MARGIN_PX } from '~/theme';
import { TenantsRefs } from '~/utils/tenantsConfig';
import { toBoolean } from '~/utils/toBoolean';

interface TabsProps {
  selectedItem?: string;
  refine?: Function;
}

type Tab = {
  testId: string;
  label: string;
  value: (typeof HITS_TYPES)[keyof typeof HITS_TYPES];
  link: string;
};

const Tabs: FC<TabsProps> = ({ selectedItem, refine }) => {
  const { t } = useTenantTranslation();

  const { searchState } = useDiscoverContext();
  const searchQuery = searchState?.query || '';
  const { tenant } = useTenantContext();
  const contentAvailability = useContentAvailability();

  const tabs = [
    {
      testId: 'videosTab',
      label: t('page.discover.videoModules'),
      value: HITS_TYPES.VIDEO,
      link: `${DISCOVER_VIDEOS_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`,
    },
    contentAvailability.pathways && {
      testId: 'pathwaysTab',
      label: t('page.discover.pathways'),
      value: HITS_TYPES.PATHWAY,
      link: `${DISCOVER_PATHWAYS_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`,
    },
    contentAvailability.insights && {
      testId: 'newsTab',
      label: t('page.discover.text'),
      value: HITS_TYPES.NEWS,
      link: `${DISCOVER_INSIGHTS_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`,
    },
    contentAvailability.glossary && {
      testId: 'glossaryTab',
      label: t('page.discover.glossary'),
      value: HITS_TYPES.GLOSSARY,
      link: `${DISCOVER_GLOSSARY_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`,
    },
    tenant?.name === TenantsRefs.Ssku && {
      testId: 'coursesTab',
      label: t('page.discover.certificatePrograms'),
      value: HITS_TYPES.COURSE,
      link: `${DISCOVER_COURSES_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`,
    },
  ].filter(toBoolean<Tab>);

  const allTab: Tab = {
    testId: 'allTab',
    value: HITS_TYPES.ALL,
    label: t('page.discover.all'),
    link: `${DISCOVER_SEARCH_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`,
  };

  if (contentAvailability.loading) {
    return (
      <Wrapper>
        <LoadingTabs xs={SKELETON_TEXT_HEIGHT_PX} />
      </Wrapper>
    );
  }

  if (tabs.length === 1) {
    return null;
  }

  return (
    <Wrapper>
      {[allTab, ...tabs].map((tab, index) => (
        <TabItem
          key={index}
          data-testid={tab.testId}
          value={tab.value}
          label={tab.label}
          selectedItem={selectedItem}
          handleSelect={refine}
          link={tab.link}
        />
      ))}
    </Wrapper>
  );
};

export default Tabs;

const Wrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  & div,
  & a {
    margin-right: ${SMALL_MARGIN_PX};

    &:last-child {
      margin-right: 0;
    }

    ${({ theme }) => theme.breakpoints.up('sm')} {
      margin-right: ${MIDDLE_MARGIN_PX};
    }
  }
`;
const LoadingTabs = styled(AnimatedSkeleton)`
  max-width: 400px;
  width: 100vw;
`;
